import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth, apiFetch } from '../App';
import ParallaxBackground from '../components/ParallaxBackground';
import IndiaMap from '../components/IndiaMap';
import { MapPin, Clock, Star, ChevronRight, Truck, Store, Shield, Sparkles, Heart, Camera, ArrowRight, Phone, Mail, Instagram, Twitter, Facebook } from 'lucide-react';

const TESTIMONIALS = [
  { name: 'Priya Sharma', pet: 'Max (Golden Retriever)', text: 'Amazing grooming service! Max always comes back looking and smelling wonderful. The live streaming is such a thoughtful feature.', rating: 5, city: 'Mumbai' },
  { name: 'Rahul Menon', pet: 'Luna (Persian Cat)', text: 'Finally found groomers who know how to handle cats gently. Luna was so relaxed. Highly recommend!', rating: 5, city: 'Hyderabad' },
  { name: 'Ananya Reddy', pet: 'Bruno (Lab)', text: 'The subscription plan saves us so much money. Bruno gets groomed every week now. Petfolk is the best!', rating: 5, city: 'Hyderabad' },
  { name: 'Vikram Patel', pet: 'Simba (Indie)', text: 'Love the mobile van service. No more stressful car rides for Simba. They come right to our doorstep!', rating: 4, city: 'Mumbai' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, setShowAuth } = useAuth();
  const [nodes, setNodes] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    apiFetch('/api/nodes').then(setNodes).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCurrentTestimonial(p => (p + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const stores = nodes
    .filter(n => (n.type === 'STORE' || n.type === 'POD') && !n.comingSoon)
    .sort((a, b) => {
      // Stores before pods, then bandra last among stores (legacy sort)
      if (a.type !== b.type) return a.type === 'STORE' ? -1 : 1;
      return (a.slug === 'bandra' ? 1 : b.slug === 'bandra' ? -1 : 0);
    });
  const vans = nodes.filter(n => n.type === 'VAN');

  return (
    <div className="relative min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Helmet>
        <title>Petfolk — Your Pet's Styling Team</title>
        <meta name="description" content="Book Grooming, Live Stream & Pickup/drop. Book In-store at Narsingi, Kompally, Rajahmundry, Bandra or Mobile Vans in Hyderabad." />
        <meta property="og:title" content="Petfolk, your Pet's styling team." />
        <meta property="og:description" content="Book Grooming, Live Stream & Pickup/drop. Book In-store at Narsingi, Kompally, Rajahmundry, Bandra or Mobile Vans in Hyderabad." />
        <meta property="og:image" content="https://static.prod-images.emergentagent.com/jobs/a6d80798-6ad8-4aec-af06-27c80904db8c/images/310b0a7694c04ffffc069eb4df4f83ce33cb3e6f5567ff4aeff01317faec743b.png" />
        <meta property="og:url" content="https://petfolk.in/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Petfolk, your Pet's styling team." />
        <meta name="twitter:description" content="Book Grooming, Live Stream & Pickup/drop. Book In-store at Narsingi, Kompally, Rajahmundry, Bandra or Mobile Vans in Hyderabad." />
        <meta name="twitter:image" content="https://static.prod-images.emergentagent.com/jobs/a6d80798-6ad8-4aec-af06-27c80904db8c/images/310b0a7694c04ffffc069eb4df4f83ce33cb3e6f5567ff4aeff01317faec743b.png" />
      </Helmet>
      <ParallaxBackground />

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" style={{ color: 'var(--text-primary)' }}>
                Your Furball's{' '}
                <span className="gradient-text">Styling Team</span>
              </h1>
              <p className="text-lg sm:text-xl mb-8 max-w-lg mx-auto lg:mx-0" style={{ color: 'var(--text-secondary)' }}>
                Expert grooming for your furry family member. Premium products, certified groomers, and love in every brush stroke.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <button
                  data-testid="hero-book-now-btn"
                  onClick={() => {
                    const firstStore = stores[0];
                    if (firstStore) navigate(`/${firstStore.slug}`);
                  }}
                  className="btn-primary text-lg px-8 py-4"
                >
                  Book Now <ArrowRight size={20} />
                </button>
                <a href="#select-store" className="btn-outline text-lg px-8 py-3.5">
                  Select Location
                </a>
              </div>
              <div className="mt-6 flex items-center gap-3 justify-center lg:justify-start">
                <Star size={18} className="text-yellow-500 fill-yellow-500" />
                <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>4.9</span>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>2000+ happy pets across India</span>
              </div>
            </div>
            {/* Hero map — replaces the photo */}
            <div className="relative flex items-center justify-center">
              <IndiaMap />
            </div>
          </div>
        </div>
      </section>

      {/* Store Selector Section */}
      <section id="select-store" data-testid="locations-section" className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              Select a <span className="gradient-text">Store</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>Choose a location near you to book a grooming session</p>
          </div>

          {/* Store Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stores.map(node => (
              <Link
                key={node.id}
                to={`/${node.slug}`}
                data-testid={`store-card-${node.slug}`}
                className="group petfolk-card overflow-hidden"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={node.heroImageUrl}
                    alt={node.displayName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {node.type === 'POD' && (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-petfolk-purple text-white shadow-lg" data-testid={`pod-badge-${node.slug}`}>
                      Pod
                    </span>
                  )}
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="text-white font-outfit font-bold text-lg">{node.displayName}</h3>
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin size={12} /> {node.localName} - {node.cityName}
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{node.address}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <Clock size={12} /> {node.operatingHours?.open} - {node.operatingHours?.close}
                    </div>
                    <span className="text-petfolk-orange font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Visit <ChevronRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Vans only — upcoming stores have launched */}
          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            {vans.map(v => (
              <Link key={v.id} to={`/${v.slug}`} data-testid={`van-card-${v.slug}`} className="petfolk-card p-6 flex items-center gap-4 group">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-petfolk-teal/20 to-petfolk-teal/5 flex items-center justify-center shrink-0">
                  <Truck size={28} className="text-petfolk-teal" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{v.displayName}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>We come to you! Mobile grooming across {v.cityName}</p>
                </div>
                <ChevronRight className="text-petfolk-teal group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
          <p className="text-center mt-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Want a Petfolk in your city? Explore our{' '}
            <Link to="/franchise" className="font-semibold hover:underline" style={{ color: 'var(--petfolk-orange)' }} data-testid="franchise-link">Franchise opportunities</Link>
          </p>
        </div>
      </section>

      {/* Service Modes */}
      <section data-testid="service-modes-section" className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            How Would You Like to <span className="gradient-text">Groom?</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Store, title: 'In-Store Experience', desc: 'Visit our premium grooming studios with state-of-the-art equipment and a spa-like ambiance for your pet.', color: 'petfolk-orange', link: '/bandra' },
              { icon: Truck, title: 'Mobile Van', desc: 'Our fully-equipped grooming vans come to your doorstep. No travel stress for your pet!', color: 'petfolk-teal', link: '/hyderabad-van-1' },
              { icon: Shield, title: 'Grooming Pod', desc: 'Compact, efficient grooming pods in your neighborhood malls and residential complexes.', color: 'petfolk-purple', link: '#' },
            ].map((mode, i) => (
              <Link key={i} to={mode.link} className="petfolk-card p-8 text-center group" data-testid={`mode-card-${i}`}>
                <div className={`w-20 h-20 rounded-3xl mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-${mode.color}/20 to-${mode.color}/5 group-hover:scale-110 transition-transform`} style={{ background: `linear-gradient(135deg, var(--${mode.color})15, var(--${mode.color})05)` }}>
                  <mode.icon size={36} style={{ color: `var(--${mode.color})` }} />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{mode.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{mode.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Petfolk */}
      <section data-testid="why-petfolk-section" className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            Why Pet Parents <span className="gradient-text">Love Us</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: 'Expert Groomers', desc: 'Certified professionals trained in breed-specific grooming techniques' },
              { icon: Shield, title: 'Premium Products', desc: 'Only the finest, pet-safe, organic grooming products for your fur baby' },
              { icon: Camera, title: 'Live Streaming', desc: 'Watch your pet\'s grooming session live from anywhere. Peace of mind included!' },
              { icon: Heart, title: 'Happy Pets', desc: '2000+ pets groomed with love. 4.9 star rating from pet parents across India' },
              { icon: Star, title: 'Loyalty Rewards', desc: 'Earn credits on every visit. Subscribe for monthly plans and save up to 15%' },
              { icon: Clock, title: 'Easy Booking', desc: 'Book in seconds via web or WhatsApp. Flexible scheduling that fits your life' },
            ].map((prop, i) => (
              <div key={i} className="petfolk-card p-6 flex gap-4" data-testid={`value-prop-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-petfolk-orange/15 to-petfolk-purple/10 flex items-center justify-center shrink-0">
                  <prop.icon size={22} className="text-petfolk-orange" />
                </div>
                <div>
                  <h3 className="font-outfit font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{prop.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{prop.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="relative py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            What Pet Parents <span className="gradient-text">Say</span>
          </h2>
          <div className="petfolk-card p-8 sm:p-10 text-center relative overflow-hidden">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(TESTIMONIALS[currentTestimonial].rating)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <p className="text-lg sm:text-xl leading-relaxed mb-6 italic" style={{ color: 'var(--text-primary)' }}>
              "{TESTIMONIALS[currentTestimonial].text}"
            </p>
            <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>
              {TESTIMONIALS[currentTestimonial].name}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {TESTIMONIALS[currentTestimonial].pet} • {TESTIMONIALS[currentTestimonial].city}
            </p>
            <div className="flex justify-center gap-2 mt-6">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTestimonial(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentTestimonial ? 'w-8 bg-petfolk-orange' : ''}`}
                  style={{ background: i !== currentTestimonial ? 'var(--border-color)' : undefined }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #8B5CF6 100%)' }}>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-4">Ready to Pamper Your Pet?</h2>
            <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">Book your first grooming session today and get 20% off with code WELCOME20</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={() => stores[0] && navigate(`/${stores[0].slug}`)} className="bg-white text-petfolk-orange font-outfit font-bold rounded-2xl px-8 py-4 hover:scale-105 transition-transform" data-testid="cta-book-btn">
                Book Now <ArrowRight size={18} className="inline ml-2" />
              </button>
              <button onClick={() => setShowAuth(true)} className="border-2 border-white text-white font-outfit font-bold rounded-2xl px-8 py-3.5 hover:bg-white/10 transition-colors" data-testid="cta-account-btn">
                Create Account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="relative py-12 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png" alt="Petfolk" className="h-8 w-auto object-contain mb-4" />
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                India's leading pet grooming franchise. Expert care, premium products, and lots of love for your furry family.
              </p>
            </div>
            <div>
              <h4 className="font-outfit font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Locations</h4>
              <ul className="space-y-2">
                {stores.filter(n => n.slug !== 'bandra').map(n => (
                  <li key={n.id}><Link to={`/${n.slug}`} className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>{n.displayName}</Link></li>
                ))}
                <li><Link to="/bandra" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Petfolk Bandra</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-outfit font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/support" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Support</Link></li>
                <li><Link to="/paws" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>My Account</Link></li>
                <li><Link to="/blog" data-testid="footer-blog-link" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Blog</Link></li>
                <li><Link to="/franchise" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Own a Franchise</Link></li>
                <li><Link to="/privacy" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Privacy Policy</Link></li>
                <li><Link to="/tnc" className="text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>Terms &amp; Conditions</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-outfit font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Contact</h4>
              <ul className="space-y-2">
                <li>
                  <a href="https://wa.me/919160153336" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>
                    <Phone size={14} /> +91 9160153336
                  </a>
                </li>
                <li>
                  <a href="mailto:support@petfolk.app" className="flex items-center gap-2 text-sm hover:text-petfolk-orange transition-colors" style={{ color: 'var(--text-secondary)' }}>
                    <Mail size={14} /> support@petfolk.app
                  </a>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.instagram.com/petfolkapp" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: 'var(--text-secondary)' }}><Instagram size={18} /></a>
                <a href="https://x.com/petfolkapp" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: 'var(--text-secondary)' }}><Twitter size={18} /></a>
                <a href="https://facebook.com/petfolkapp" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: 'var(--text-secondary)' }}><Facebook size={18} /></a>
              </div>
            </div>
          </div>
          <div className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <p>&copy; 2026 Petfolk. All rights reserved. Made with love for pets across India.</p>
            <div className="flex gap-4">
              <Link to="/tnc" className="hover:text-petfolk-orange transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-petfolk-orange transition-colors">Privacy</Link>
              <Link to="/support" className="hover:text-petfolk-orange transition-colors">Support</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
